// src/components/profile/ProfileUpdateCard.tsx
import React, { useState, useEffect } from 'react';
import {
   Card,
   CardContent,
   Box,
   Typography,
   Button,
   LinearProgress,
   IconButton,
} from '@mui/material';
import {
   Person as PersonIcon,
   Language as LanguageIcon,
   Description as DescriptionIcon,
   PhotoCamera as PhotoCameraIcon,
   EditOutlined as EditIcon,
   ArrowBackIos as ArrowBackIcon,
   ArrowForwardIos as ArrowForwardIcon,
   Business as BusinessIcon,
   Public as WebsiteIcon,
   Phone as PhoneIcon,
   Label as TaglineIcon,
   LocationOn as LocationIcon,
   Work as WorkIcon,
   Search as LookingForIcon,
   Star as FeaturedIcon,
   Psychology as InterestsIcon,
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import SingleFieldEditModal from './SingleFieldEditModal';

export interface ProfileCompletionStatus {
   hasDisplayName: boolean;
   hasPhotoURL: boolean;
   hasBio: boolean;
   hasLanguages: boolean;
   hasTagline: boolean;
   hasCompanyName: boolean;
   hasWebsite: boolean;
   hasPhoneNumber: boolean;
   hasLocation: boolean;
   hasWhatIDo: boolean;
   hasLookingFor: boolean;
   hasFeaturedProjects: boolean;
   hasInterests: boolean;
   completionPercentage: number;
   missingFields: string[];
}

interface ProfileUpdateCardProps {
   profileStatus: ProfileCompletionStatus;
   userId: string;
   onEditClick?: () => void; // Optional - not used anymore
   onProfileRefresh?: () => Promise<void>; // Callback to refresh profile status after save
}

const ProfileUpdateCard: React.FC<ProfileUpdateCardProps> = ({ 
   profileStatus, 
   userId, 
   onEditClick, // Not used anymore but kept for backwards compatibility
   onProfileRefresh 
}) => {
   const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
   const [modalOpen, setModalOpen] = useState(false);
   const [currentUserData, setCurrentUserData] = useState<any>(null);

   // Fetch current user data for modal
   useEffect(() => {
      const fetchUserData = async () => {
         try {
            // Fetch from userProfiles to get all profile data including bio and languages
            const userProfileDoc = await getDoc(doc(db, 'userProfiles', userId));
            if (userProfileDoc.exists()) {
               setCurrentUserData(userProfileDoc.data());
            }
         } catch (error) {
            console.error('Error fetching user data:', error);
         }
      };
      if (userId) {
         fetchUserData();
      }
   }, [userId]);

   const handleCompleteProfile = () => {
      setModalOpen(true);
   };

   const handleModalClose = () => {
      setModalOpen(false);
   };

   const handleFieldSaved = async () => {
      // Refresh user data and trigger profile status refresh
      try {
         // Fetch from userProfiles to get all profile data including bio and languages
         const userProfileDoc = await getDoc(doc(db, 'userProfiles', userId));
         if (userProfileDoc.exists()) {
            setCurrentUserData(userProfileDoc.data());
         }
         
         // Call the parent's refresh callback to update profile completion status
         if (onProfileRefresh) {
            await onProfileRefresh();
         }
         
         // Move to the next field in the carousel if there are remaining fields
         // After the parent updates profileStatus, missingFieldsInfo will be recalculated
         // and the current index might be out of bounds, so reset to 0
         setCurrentFieldIndex(0);
      } catch (error) {
         console.error('Error refreshing user data:', error);
      }
   };

   const fieldInfo = [
      {
         field: 'hasDisplayName',
         label: 'Display Name',
         icon: <PersonIcon />,
         description: 'Add your name so people know who you are',
      },
      {
         field: 'hasPhotoURL',
         label: 'Profile Photo',
         icon: <PhotoCameraIcon />,
         description: 'Upload a photo to personalize your profile',
      },
      {
         field: 'hasBio',
         label: 'Bio',
         icon: <DescriptionIcon />,
         description: 'Tell others about yourself and your interests',
      },
      {
         field: 'hasLocation',
         label: 'Location',
         icon: <LocationIcon />,
         description: 'Share where you are based',
      },
      {
         field: 'hasLanguages',
         label: 'Languages',
         icon: <LanguageIcon />,
         description: 'Share which languages you speak',
      },
      {
         field: 'hasTagline',
         label: 'Tagline',
         icon: <TaglineIcon />,
         description: 'Add a catchy tagline to describe yourself',
      },
      {
         field: 'hasCompanyName',
         label: 'Company',
         icon: <BusinessIcon />,
         description: 'Share where you work or your company name',
      },
      {
         field: 'hasWebsite',
         label: 'Website',
         icon: <WebsiteIcon />,
         description: 'Add your personal or professional website',
      },
      {
         field: 'hasPhoneNumber',
         label: 'Phone Number',
         icon: <PhoneIcon />,
         description: 'Provide your contact phone number',
      },
      {
         field: 'hasWhatIDo',
         label: 'What I Do',
         icon: <WorkIcon />,
         description: 'Highlight what you do professionally',
      },
      {
         field: 'hasLookingFor',
         label: 'Looking For',
         icon: <LookingForIcon />,
         description: 'Share what opportunities you are seeking',
      },
      {
         field: 'hasFeaturedProjects',
         label: 'Featured Projects',
         icon: <FeaturedIcon />,
         description: 'Showcase your best work and projects',
      },
      {
         field: 'hasInterests',
         label: 'Interests & Expertise',
         icon: <InterestsIcon />,
         description: 'Add your interests and areas of expertise',
      },
   ];

   // Only show missing fields
   const missingFieldsInfo = fieldInfo.filter(
      (info) => !profileStatus[info.field as keyof ProfileCompletionStatus]
   );

   // Don't render if profile is complete or no missing fields
   if (profileStatus.completionPercentage === 100 || missingFieldsInfo.length === 0) {
      return null;
   }

   // Ensure currentFieldIndex is within bounds
   const safeCurrentIndex = Math.min(currentFieldIndex, missingFieldsInfo.length - 1);

   // Handle navigation
   const handleNext = () => {
      if (safeCurrentIndex < missingFieldsInfo.length - 1) {
         setCurrentFieldIndex(safeCurrentIndex + 1);
      }
   };

   const handlePrevious = () => {
      if (safeCurrentIndex > 0) {
         setCurrentFieldIndex(safeCurrentIndex - 1);
      }
   };

   // Get current field to display
   const currentField = missingFieldsInfo[safeCurrentIndex];

   // Map field to type for modal
   const getFieldType = (field: string): 'displayName' | 'photoURL' | 'bio' | 'languages' | 'tagline' | 'companyName' | 'website' | 'phoneNumber' | 'location' | 'whatIDo' | 'lookingFor' | 'featuredProjects' | 'interests' => {
      switch (field) {
         case 'hasDisplayName':
            return 'displayName';
         case 'hasPhotoURL':
            return 'photoURL';
         case 'hasBio':
            return 'bio';
         case 'hasLanguages':
            return 'languages';
         case 'hasTagline':
            return 'tagline';
         case 'hasCompanyName':
            return 'companyName';
         case 'hasWebsite':
            return 'website';
         case 'hasPhoneNumber':
            return 'phoneNumber';
         case 'hasLocation':
            return 'location';
         case 'hasWhatIDo':
            return 'whatIDo';
         case 'hasLookingFor':
            return 'lookingFor';
         case 'hasFeaturedProjects':
            return 'featuredProjects';
         case 'hasInterests':
            return 'interests';
         default:
            return 'displayName';
      }
   };

   // Get current value for the field
   const getCurrentValue = () => {
      if (!currentUserData) return '';
      const fieldType = getFieldType(currentField.field);
      return currentUserData[fieldType] || (fieldType === 'languages' ? [] : '');
   };

   return (
      <>
         <SingleFieldEditModal
            open={modalOpen}
            onClose={handleModalClose}
            userId={userId}
            fieldType={getFieldType(currentField.field)}
            currentValue={getCurrentValue()}
            onSave={handleFieldSaved}
         />
      <Card
         sx={{
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 196, 167, 0.08)',
            background: '#00C4A7',
            color: 'white',
            overflow: 'hidden',
         }}
      >
         <CardContent sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
               <Typography variant='subtitle1' fontWeight='600' gutterBottom sx={{ color: 'white' }}>
                     Complete Your Profile
                  </Typography>
               <Typography variant='body2' sx={{ mb: 1.5, color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.813rem' }}>
                  Add more information to help others connect with you.
                  </Typography>

                  {/* Progress Bar */}
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                     <LinearProgress
                        variant='determinate'
                        value={profileStatus.completionPercentage}
                        sx={{
                           flex: 1,
                           height: 6,
                           borderRadius: 3,
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                           '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                           background: 'white',
                           },
                        }}
                     />
                  <Typography 
                     variant='body2' 
                     fontWeight='600' 
                     sx={{ 
                        color: 'white',
                        minWidth: '40px',
                        textAlign: 'right',
                        fontSize: '0.875rem',
                     }}
                  >
                        {profileStatus.completionPercentage}%
                     </Typography>
               </Box>
            </Box>

            {/* Current Field - Carousel Style */}
            <Box sx={{ mb: 2, position: 'relative' }}>
               {/* Navigation Indicators */}
               <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                  {missingFieldsInfo.map((_, index) => (
                     <Box
                        key={index}
                        sx={{
                           width: index === safeCurrentIndex ? 20 : 6,
                           height: 6,
                           borderRadius: 3,
                           bgcolor: index === safeCurrentIndex ? 'white' : 'rgba(255, 255, 255, 0.3)',
                           cursor: 'pointer',
                           transition: 'all 0.3s ease',
                           '&:hover': {
                              bgcolor: index === safeCurrentIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                              transform: 'scale(1.1)',
                           },
                        }}
                        onClick={() => setCurrentFieldIndex(index)}
                     />
                  ))}
               </Box>

               {/* Current Field Card */}
               <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                     gap: 1.5,
                     p: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                     borderRadius: 2,
                     boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                     transition: 'all 0.3s ease',
                     backdropFilter: 'blur(10px)',
                  }}
               >
                  {/* Previous Button */}
                  <IconButton
                     onClick={handlePrevious}
                     disabled={safeCurrentIndex === 0}
                     sx={{
                        bgcolor: 'rgba(0, 196, 167, 0.1)',
                        color: '#00C4A7',
                        width: 32,
                        height: 32,
                        '&:hover': { bgcolor: 'rgba(0, 196, 167, 0.2)' },
                        '&.Mui-disabled': { opacity: 0.3 },
                     }}
                     size='small'
                  >
                     <ArrowBackIcon sx={{ fontSize: 16 }} />
                  </IconButton>

                  {/* Field Icon */}
                     <Box
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                        width: 48,
                        height: 48,
                           borderRadius: '50%',
                        background: '#00C4A7',
                        color: 'white',
                        boxShadow: '0 2px 8px rgba(0, 196, 167, 0.3)',
                        }}
                     >
                     {React.cloneElement(currentField.icon, { sx: { fontSize: 24 } })}
                     </Box>

                  {/* Field Content */}
                     <Box sx={{ flex: 1 }}>
                     <Typography variant='body2' fontWeight='600' sx={{ mb: 0.5, color: '#1f2937' }}>
                        {currentField.label}
                        </Typography>
                     <Typography variant='caption' sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                        {currentField.description}
                        </Typography>
                  </Box>

                  {/* Next Button */}
                  <IconButton
                     onClick={handleNext}
                     disabled={safeCurrentIndex === missingFieldsInfo.length - 1}
                     sx={{
                        bgcolor: 'rgba(0, 196, 167, 0.1)',
                        color: '#00C4A7',
                        width: 32,
                        height: 32,
                        '&:hover': { bgcolor: 'rgba(0, 196, 167, 0.2)' },
                        '&.Mui-disabled': { opacity: 0.3 },
                     }}
                     size='small'
                  >
                     <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
               </Box>

               {/* Step Counter */}
               <Typography
                  variant='caption'
                  fontWeight='500'
                  sx={{
                     display: 'block',
                     textAlign: 'center',
                     mt: 1.5,
                     color: 'rgba(255, 255, 255, 0.9)',
                     fontSize: '0.75rem',
                  }}
               >
                  {safeCurrentIndex + 1} of {missingFieldsInfo.length} fields remaining
               </Typography>
            </Box>

            {/* Action Button */}
            <Button
               variant='contained'
               fullWidth
               size='medium'
               startIcon={<EditIcon sx={{ fontSize: 18 }} />}
               onClick={handleCompleteProfile}
               sx={{
                  py: 1.25,
                  fontSize: '0.938rem',
                  fontWeight: '600',
                  borderRadius: 2,
                  background: 'white',
                  color: '#00C4A7',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  textTransform: 'none',
                  '&:hover': {
                     background: 'rgba(255, 255, 255, 0.95)',
                     transform: 'translateY(-1px)',
                     boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                  },
                  transition: 'all 0.3s ease',
               }}
            >
               Add {currentField.label} Now
            </Button>
         </CardContent>
      </Card>
      </>
   );
};

export default ProfileUpdateCard;

